import { Icon, MenuItem, PopupMenu } from '@pandiafi/design-system'
import classNames from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  Contract,
  FeatureFlag,
  UserMetadata,
} from '../../../common/validations'
import { ContractSelector } from './components/ContractSelector/ContractSelector'
import LanguageSelector from './components/LanguageSelector'
import styles from './Navbar.module.css'
import { UserMenuButton } from './components/UserMenuButton/UserMenuButton'
import { navbarStyles } from './utils/navbarStyles'
import { ScreenReaderHint } from './components/ScreenReaderHint/ScreenReaderHint'
import { useNavbarLinks } from './hooks/useNavbarLinks/useNavbarLinks'
import { LinkSection } from './components/LinkSection/LinkSection'
import routePath from '../../router/routePath'

import { Breakpoint } from 'components'
import { useBodyScroll } from 'hooks'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'

interface NavbarProps {
  featureFlags?: FeatureFlag
  contracts?: Contract[]
  userMetadata?: UserMetadata
}

export const Navbar: React.FC<NavbarProps> = ({
  featureFlags,
  contracts,
  userMetadata,
}) => {
  const { t } = useTranslation(['common'])
  const [showNavMenu, setShowNavMenu] = useState(false)
  const [showProfile, setShowProfile] = useState(false)
  const { userInfoPath } = useContractPaths()
  const menuRef = useRef<HTMLButtonElement>(null)
  const profileRef = useRef<HTMLButtonElement>(null)
  const navPanelRef = useRef<HTMLDivElement>(null)
  const {
    enable: enableBodyScroll,
    disable: disableBodyScroll,
    scrollBarCompensation,
    enabled: bodyScrollEnabled,
  } = useBodyScroll()
  const navigate = useNavigate()
  const { homeLinks, transactionsLinks, userInfoLinks, propertyLinks } =
    useNavbarLinks(featureFlags)

  useEffect(() => {
    if (showNavMenu) {
      disableBodyScroll()
      navPanelRef.current?.scrollTo({ top: 0 })
    } else {
      enableBodyScroll()
    }
    return () => {
      enableBodyScroll()
    }
  }, [showNavMenu, enableBodyScroll, disableBodyScroll])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement

      if (showNavMenu && menuRef.current && !menuRef.current.contains(target)) {
        setShowNavMenu(false)
      }

      if (
        showProfile &&
        profileRef.current &&
        !profileRef.current.contains(target)
      ) {
        setShowProfile(false)
      }
    }

    document.addEventListener('click', handleClick)
    return () => {
      document.removeEventListener('click', handleClick)
    }
  }, [showNavMenu, showProfile])

  return (
    <header
      className={navbarStyles.wrapper}
      style={{
        paddingRight: bodyScrollEnabled ? 0 : scrollBarCompensation,
      }}
    >
      <nav className={navbarStyles.nav}>
        <div
          className={classNames(navbarStyles.navItemsLayout, 'justify-between')}
        >
          <button
            ref={menuRef}
            type="button"
            aria-expanded={showNavMenu}
            className="rounded-md p-2 text-brand-6"
            onClick={() => setShowNavMenu(!showNavMenu)}
          >
            <ScreenReaderHint hint={t('common:navbar.openMenu')} />
            <Icon
              icon={showNavMenu ? 'x-mark' : 'menu'}
              size="lg"
              className="text-brand-6 w-6 fill-current pointer-events-none"
            />
          </button>

          <ContractSelector contracts={contracts} userMetadata={userMetadata} />

          <div className="desktop:flex desktop:items-center desktop:space-x-4">
            <Breakpoint show desktop>
              <LanguageSelector />
            </Breakpoint>

            <div className={navbarStyles.userMenu}>
              <UserMenuButton
                ref={profileRef}
                toggleUserMenu={() => setShowProfile((prev) => !prev)}
                screenReaderHint={t('common:navbar.openProfile')}
              />

              <PopupMenu
                show={showProfile}
                location="bottom-right"
                className="rounded-md bg-white/95 p-2 text-brand-6 text-right"
              >
                <MenuItem
                  small
                  className="bg-transparent"
                  onClick={() => navigate(userInfoPath)}
                >
                  {t('common:views.userInfo')}
                  <Icon icon="user-outline" className="ml-1" />
                </MenuItem>

                <MenuItem
                  small
                  className="bg-transparent"
                  onClick={() => navigate(routePath.logout)}
                >
                  {t('logOut')}
                  <Icon icon="logout" className="ml-1" />
                </MenuItem>

                <Breakpoint show phone tablet>
                  <LanguageSelector mobile className="mt-4" />
                </Breakpoint>
              </PopupMenu>
            </div>
          </div>
        </div>
        <div className="relative z-50">
          <div
            className={classNames(
              'fixed inset-0 desktop:top-16 bg-black/30',
              styles.backdrop,
              showNavMenu && styles.backdropOpen
            )}
            aria-hidden="true"
          />
          <div
            className={classNames(
              'fixed inset-0 desktop:absolute desktop:inset-y-auto z-50 flex',
              styles.navWrapper
            )}
          >
            <div
              ref={navPanelRef}
              className={classNames(
                'phone:w-4/5 tablet:w-2/5 desktop:w-full overflow-y-auto overscroll-contain text-sm text-brand-6 bg-white/95 shadow-xl',
                styles.navPanel,
                showNavMenu && styles.navPanelOpen
              )}
            >
              <div className="flex justify-end">
                <button
                  type="button"
                  className="desktop:hidden p-5 text-brand-6"
                  onClick={() => setShowNavMenu(false)}
                >
                  <ScreenReaderHint hint={t('common:navbar.openMenu')} />
                  <Icon icon="x-mark" size="lg" className="text-brand-6" />
                </button>
              </div>
              <div className="mx-auto max-w-7xl px-8 py-12 text-xl">
                <div className="mx-auto max-w-4xl grid grid-cols-1 desktop:grid-cols-3 gap-8 desktop:gap-12">
                  <div className="flex items-center gap-4 pb-8">
                    <Icon icon="house" size="2x" />
                    <LinkSection links={homeLinks} />
                  </div>
                </div>

                <div className="mx-auto max-w-4xl grid grid-cols-1 desktop:grid-cols-3 gap-8 desktop:gap-12 font-light text-lg">
                  <div className="flex flex-col gap-4 desktop:gap-6">
                    <LinkSection
                      title={t('common:navbar.transactions')}
                      links={transactionsLinks}
                    />
                  </div>
                  <div className="flex flex-col gap-4 desktop:gap-6">
                    <LinkSection
                      title={t('common:navbar.userInfo')}
                      links={userInfoLinks}
                    />
                  </div>
                  <div className="flex flex-col gap-4 desktop:gap-6">
                    <LinkSection
                      title={t('common:navbar.ownProperty')}
                      links={propertyLinks}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  )
}
